import { isNewerSeq, type FeedLotState } from '@auctisync/engine-wire';
import type { FeedLot, YouStanding } from './engine-feed.js';

/**
 * Which sound a lot's change makes — THE rule, for the lot page and the Lots
 * page, website and app alike (2026-10-08). Pure: the page plays the sound.
 *
 * Jim, 2026-10-07: bid sounds play for lots SHOWING ON THE PAGE at the time,
 * not for every bid in the sale (memory: feedback_lot_pages_must_be_live).
 *
 *   - `gavel` — a lot on screen closes (open → a hammer state). A cancelled
 *     lot did not sell or fail to sell; no hammer fell, so no gavel.
 *   - `bid`   — a bid landed on a lot on screen, and it was not the viewer's.
 *   - null    — anything else, and always for a lot off screen or seen for
 *     the first time (opening a page must not chime).
 *
 * "THE VIEWER'S OWN" COMES FROM THE ENGINE. The website guessed it with a
 * 4-second window after pressing Bid: a rival's bid landing inside the
 * window was silenced, and the viewer's own bid chimed whenever its echo took
 * longer than four seconds to come back. The engine knows: it sends the
 * bidder a `you` whenever their standing moves, BEFORE the `lot` of the same
 * change and inside it (contract, `YouMessage`). So a change is the viewer's
 * own when their standing moved within the changes this update covers —
 * `you.seq` in (prev.seq, next.seq] — and the move was not losing the lead
 * (`outbid`). A rival's bid that the viewer's ceiling simply answered moves
 * nothing of theirs, sends no `you`, and chimes — they should hear that the
 * lot is contested.
 */
export type LotSound = 'bid' | 'gavel' | null;

/** The fields the rule reads; a `FeedLot` fits. */
export type SoundLot = Pick<
  FeedLot,
  'lot_id' | 'state' | 'bid_count' | 'current_high_cents' | 'last_bid_at' | 'seq'
>;

export interface LotSoundInput {
  /** What the page held for the lot before this change; undefined = first sight. */
  prev: SoundLot | undefined;
  next: SoundLot;
  /** The lot is on screen now. */
  onScreen: boolean;
  /** The viewer's standing on the lot (`feed.you(id)`); undefined when none or signed out. */
  you: YouStanding | undefined;
}

const STILL_OPEN: ReadonlySet<FeedLotState> = new Set<FeedLotState>([
  'pending',
  'open',
  'bin_pending',
  'closing',
]);
const HAMMER: ReadonlySet<FeedLotState> = new Set<FeedLotState>([
  'closed_sold',
  'closed_no_winner',
  'closed_bin_sold',
  'closed_no_sale',
]);

const later = (a: string, b: string): boolean => {
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  return Number.isFinite(ta) && Number.isFinite(tb) && ta > tb;
};

/**
 * Did a bid land between `prev` and `next`? Any one visible sign says so:
 * the count rose, the price rose (or appeared), or the last-bid time moved
 * on. A house that hides all three from this viewer gets no bid sound — a
 * chime would tell them what the house chose not to.
 */
export function bidLanded(prev: SoundLot, next: SoundLot): boolean {
  if (prev.bid_count !== null && next.bid_count !== null && next.bid_count > prev.bid_count) {
    return true;
  }
  if (next.current_high_cents !== null) {
    if (prev.current_high_cents === null) return true;
    if (next.current_high_cents > prev.current_high_cents) return true;
  }
  // `undefined` = not known (a summary line): no evidence either way.
  if (prev.last_bid_at !== undefined && typeof next.last_bid_at === 'string') {
    if (prev.last_bid_at === null || later(next.last_bid_at, prev.last_bid_at)) return true;
  }
  return false;
}

/** Was this change the viewer's own doing? See the header. */
export function isOwnChange(prev: SoundLot, next: SoundLot, you: YouStanding | undefined): boolean {
  if (you === undefined || you.lot_id !== next.lot_id || you.outbid) return false;
  return isNewerSeq(you.seq, prev.seq) && !isNewerSeq(you.seq, next.seq);
}

export function lotSoundFor({ prev, next, onScreen, you }: LotSoundInput): LotSound {
  if (!onScreen || prev === undefined || prev.lot_id !== next.lot_id) return null;
  if (STILL_OPEN.has(prev.state) && HAMMER.has(next.state)) return 'gavel';
  if (!bidLanded(prev, next)) return null;
  return isOwnChange(prev, next, you) ? null : 'bid';
}

// ─── The sounds a bidder can choose (2026-10-11) ───────────────────────────

/**
 * The bid sounds on offer — the ids and the names, ONCE, for the website and
 * the app (2026-10-11, when the app began to play them). The files are each
 * surface's own copy of the same bytes: the website serves
 * `/sounds/<id>.m4a`, the app bundles `assets/sounds/<id>.m4a`, and a test
 * holds the two sets identical. The gavel is not a choice; it is `gavel.m4a`.
 */
export const BID_SOUNDS = [
  { id: 'happy-beeps', label: 'Happy Beeps' },
  { id: 'sunny-chirp', label: 'Sunny Chirp' },
  { id: 'robo-confirm', label: 'Robo Confirm' },
  { id: 'well-done', label: 'Well done!' },
  { id: 'chill-bells', label: 'Chill Bells' },
  { id: 'alert-bells', label: 'Alert Bells' },
  { id: 'notification-bell', label: 'Notification Bell' },
  { id: 'sent-chime', label: 'Sent Chime' },
  { id: 'confirm-chirp', label: 'Confirm Chirp' },
] as const;

export type BidSoundId = (typeof BID_SOUNDS)[number]['id'];

export const DEFAULT_BID_SOUND: BidSoundId = 'happy-beeps';

/** A stored choice back to a sound on offer; anything else is the default. */
export function toBidSoundId(raw: unknown): BidSoundId {
  return BID_SOUNDS.some((s) => s.id === raw) ? (raw as BidSoundId) : DEFAULT_BID_SOUND;
}

/**
 * Whether bid sounds start OFF before the bidder has chosen: for a reader who
 * asked their device for reduced motion (help: "bid sounds start off"), and
 * only then. A stored choice always wins.
 */
export function bidSoundsStartMuted(reduceMotion: boolean): boolean {
  return reduceMotion;
}
